import { Button } from '@savvy-kit/components/button';
import { Skeleton } from '@savvy-kit/components/skeleton';
import { Text } from '@savvy-kit/foundations/text';
import { ChartContainer, ChartTooltip } from '@savvy-kit/patterns/chart';
import { isNil } from 'es-toolkit';
import { RotateCw } from 'lucide-react';
import { Area, AreaChart, XAxis, YAxis } from 'recharts';

import type { ChartConfig } from '@savvy-kit/patterns/chart';

import type { NetWorthPoint } from './net-worth';
import { chartSpansMultipleYears, formatChartTickDate, safeLargeMoney, safeMoney } from '../common/utils';
import {
  BALANCE_CHART_ERROR_DESCRIPTION,
  BALANCE_CHART_ERROR_RETRY_LABEL,
  BALANCE_CHART_ERROR_TITLE,
  BALANCE_CHART_SHORT_RANGE_DESCRIPTION,
} from './constants';
import { paddedDomain } from './net-worth';
import { netWorthPointDateLabel } from './utils';

const CHART_HEIGHT_CLASS = 'h-64';
const AXIS_TICK = { fontSize: 11, fill: 'var(--text-secondary)' };
const AXIS_WIDTH = 56;
type BalanceTooltipProps = {
  readonly active?: boolean;
  readonly payload?: readonly { readonly payload?: NetWorthPoint }[];
};

function BalanceTooltip({ active, payload }: BalanceTooltipProps) {
  const point = payload?.[0]?.payload;
  if (active !== true || isNil(point)) {
    return null;
  }

  return (
    <div className="max-w-[calc(100vw-7rem)] rounded-lg border border-border-light bg-popover px-4 py-3 shadow-md md:min-w-52">
      <p className="text-sm text-secondary">{netWorthPointDateLabel(point.date)}</p>
      <p className="text-xl font-semibold tabular-nums text-dark">{safeMoney(point.amount)}</p>
    </div>
  );
}

type BalanceHistoryChartProps = {
  readonly points: readonly NetWorthPoint[];
  readonly seriesLabel: string;
  readonly chartLabel: string;
  readonly gradientId: string;
};

function BalanceHistoryChart({ points, seriesLabel, chartLabel, gradientId }: BalanceHistoryChartProps) {
  const config = { balance: { label: seriesLabel, color: 'var(--graph-line-accent)' } } satisfies ChartConfig;
  const withYear = chartSpansMultipleYears(points);

  return (
    <ChartContainer
      config={config}
      role="img"
      aria-label={chartLabel}
      className={`aspect-auto w-full touch-pan-y ${CHART_HEIGHT_CLASS}`}
    >
      <AreaChart data={[...points]} margin={{ top: 8, right: 0, bottom: 0, left: 0 }}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--color-balance)" stopOpacity={0.28} />
            <stop offset="100%" stopColor="var(--color-balance)" stopOpacity={0} />
          </linearGradient>
        </defs>
        <XAxis
          dataKey="date"
          tick={AXIS_TICK}
          tickLine={false}
          axisLine={false}
          tickMargin={12}
          minTickGap={56}
          interval="preserveStartEnd"
          tickFormatter={(isoDate: string) => formatChartTickDate(isoDate, withYear)}
        />
        <YAxis
          dataKey="amount"
          domain={paddedDomain(points)}
          tick={AXIS_TICK}
          tickLine={false}
          axisLine={false}
          width={AXIS_WIDTH}
          tickFormatter={(amount: number) => safeLargeMoney(amount)}
        />
        <ChartTooltip content={<BalanceTooltip />} />
        <Area
          dataKey="amount"
          type="monotone"
          stroke="var(--color-balance)"
          strokeWidth={1.5}
          fill={`url(#${gradientId})`}
          fillOpacity={1}
          dot={false}
          isAnimationActive={false}
        />
      </AreaChart>
    </ChartContainer>
  );
}

type BalanceHistoryChartSlotProps = {
  readonly loading: boolean;
  readonly failed: boolean;
  readonly points: readonly NetWorthPoint[];
  readonly onRetry: () => void;
  readonly seriesLabel: string;
  readonly chartLabel: string;
  readonly gradientId: string;
  readonly emptyTitle: string;
  readonly emptyDescription: string;
  readonly testIdPrefix: string;
};

export function BalanceHistoryChartSlot({
  loading,
  failed,
  points,
  onRetry,
  seriesLabel,
  chartLabel,
  gradientId,
  emptyTitle,
  emptyDescription,
  testIdPrefix,
}: BalanceHistoryChartSlotProps) {
  if (loading) {
    return <Skeleton className={`mx-4 ${CHART_HEIGHT_CLASS}`} />;
  }

  if (failed) {
    return (
      <div
        className={`flex ${CHART_HEIGHT_CLASS} flex-col items-center justify-center gap-2 px-6 text-center`}
        data-testid={`${testIdPrefix}-chart-error`}
      >
        <Text variant="subtitle-sm" color="dark">
          {BALANCE_CHART_ERROR_TITLE}
        </Text>
        <Text variant="body-sm">{BALANCE_CHART_ERROR_DESCRIPTION}</Text>
        <Button variant="secondary" size="sm" onClick={onRetry}>
          <RotateCw size={14} aria-hidden="true" />
          {BALANCE_CHART_ERROR_RETRY_LABEL}
        </Button>
      </div>
    );
  }

  if (points.length === 0) {
    return (
      <div
        className={`flex ${CHART_HEIGHT_CLASS} flex-col items-center justify-center gap-2 px-6 text-center`}
        data-testid={`${testIdPrefix}-chart-empty`}
      >
        <Text variant="subtitle-sm" color="dark">
          {emptyTitle}
        </Text>
        <Text variant="body-sm">{emptyDescription}</Text>
      </div>
    );
  }

  if (points.length < 2) {
    return (
      <div
        className={`flex ${CHART_HEIGHT_CLASS} items-center justify-center px-6 text-center`}
        data-testid={`${testIdPrefix}-chart-short-range`}
      >
        <Text variant="body-sm">{BALANCE_CHART_SHORT_RANGE_DESCRIPTION}</Text>
      </div>
    );
  }

  return (
    <BalanceHistoryChart points={points} seriesLabel={seriesLabel} chartLabel={chartLabel} gradientId={gradientId} />
  );
}
